import {
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Skeleton,
  Space,
  Typography,
  Upload,
} from "antd";
import axios from "axios";
import CreateOrEditContact from "components/EditCompanyContacts/CompanyContactBox/CreateOrEditContact";
import { message } from "@bindeleddet/core/utils";
import { CompanyContact, Sector, Job } from "@bindeleddet/core/types";
import { uploadButton } from "components/UploadButton/UploadButton";
import { JOB_TYPE_DESCRIPTIONS, yearLevels } from "constants/jobTypes";
import dayjs from "dayjs";
import "dayjs/locale/nb";
import { getBase64 } from "helpers/functions";
import { IMAGE_ACCEPT, beforeUploadImage } from "helpers/fileValidation";
import React, { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import { Link, useHistory, useParams } from "react-router-dom";

const { Paragraph, Text, Title } = Typography;
const { Option } = Select;

interface Props {
  existingJob?: boolean;
}

const AdminJobs: React.FC<Props> = ({ existingJob }) => {
  const [form] = Form.useForm();
  const [companyContacts, setCompanyContacts] = useState<CompanyContact[]>([]);
  const [showCreateContactModal, setShowCreateContactModal] = useState<boolean>(false);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const history = useHistory();
  const [job, setJob] = useState<Job>();
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [removeLogo, setRemoveLogo] = useState<boolean>(false);
  const [attachment, setAttachment] = useState<File | null>(null);
  const [attachmentPreview, setAttachmentPreview] = useState<string | null>(null);
  const [removeAttachment, setRemoveAttachment] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);

  const { id, jobId } = useParams<{ id: string; jobId: string }>();

  useEffect(() => {
    if (existingJob) {
      axios.get<Job>(`/jobs/${id}/job-list/${jobId}/`).then(response => {
        setJob(response.data);
        setLogo(null);
        setLogoPreview(null);
        setRemoveLogo(false);
        setAttachment(null);
        setAttachmentPreview(null);
        setRemoveAttachment(false);
      });
    }
  }, [id, jobId, existingJob]);

  useEffect(() => {
    if (!id || (existingJob && !job)) return;
    axios.get(`/companies/${existingJob ? job?.company : id}/contact-persons/`).then(response => {
      setCompanyContacts(response.data);
    });
  }, [id, existingJob, job]);

  useEffect(() => {
    axios.get<Sector[]>("/companies/sectors/").then(response => {
      setSectors(response.data);
    });
  }, []);

  const createJobPosting = (values: Job) => {
    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (!value || key === "deadline") return;
      if (value instanceof Array) {
        value.forEach(v => formData.append(key, String(v)));
        return;
      }
      formData.append(key, String(value));
    });
    formData.append("created_at", new Date().toISOString());
    formData.append("updated_at", new Date().toISOString());
    formData.append("company", id);
    formData.append("deadline", dayjs(values.deadline).endOf("day").toISOString());
    if (logo) formData.append("alt_logo", logo);
    if (attachment) formData.append("attachment", attachment);

    setLoading(true);
    axios
      .post<Job>(`/jobs/${id}/job-list/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        history.push(`/admin/companies/${id}/`);
        message("success", "Stillingsannonse opprettet");
        setLoading(false);
      })
      .catch(() => {
        message("error", "Kunne ikke opprette stillingsannonse");
        // TODO handle errors from backend when backend it set up
        setLoading(false);
      });
  };

  const updateJobPosting = (values: Job) => {
    if (!job) return;

    const formData = new FormData();
    Object.entries(values).forEach(([key, value]) => {
      if (!value || key === "deadline") return;
      if (value instanceof Array) {
        value.forEach(v => formData.append(key, String(v)));
        return;
      }
      formData.append(key, String(value));
    });
    formData.append("updated_at", new Date().toISOString());
    formData.append("deadline", dayjs(values.deadline).endOf("day").toISOString());
    if (logo) formData.append("alt_logo", logo);
    if (removeLogo) formData.append("alt_logo", "");
    if (attachment) formData.append("attachment", attachment);
    if (removeAttachment) formData.append("attachment", "");

    setLoading(true);
    axios
      .patch<Job>(`/jobs/${id}/job-list/${jobId}/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      .then(() => {
        history.push(`/admin/companies/${id}/`);
        message("success", "Stillingsannonse oppdatert");
        setLoading(false);
      })
      .catch(() => {
        message("error", "Kunne ikke oppdatere stillingsannonse");
        setLoading(false);
      });
  };

  const checkboxSectors = sectors.map(sector => {
    return (
      <Col key={sector.id} lg={4} md={6} sm={8} xs={12}>
        <Checkbox key={sector.id} value={sector.id}>
          {sector.name}
        </Checkbox>
      </Col>
    );
  });

  useEffect(() => {
    if (logo) {
      getBase64(logo)
        .then(url => setLogoPreview(url))
        .catch(() => setLogoPreview(null));
    }
    if (attachment) {
      getBase64(attachment)
        .then(url => setAttachmentPreview(url))
        .catch(() => setAttachmentPreview(null));
    }
  }, [logo, attachment]);

  if (existingJob && !job) return <Skeleton active />;

  return (
    <>
      <Title level={2}>{existingJob ? "Rediger stillingsannonse" : "Opprett ny stillingsannonse"}</Title>
      <Card>
        <Form
          form={form}
          layout="vertical"
          requiredMark
          scrollToFirstError
          onFinish={existingJob ? updateJobPosting : createJobPosting}
        >
          <Form.Item
            name="title"
            label="Tittel"
            initialValue={job?.title}
            rules={[
              { required: true, message: "Vennligst skriv inn tittel" },
              { max: 100, message: "Tittel kan ikke være lengre enn 100 tegn" },
            ]}
          >
            <Input placeholder="Navn på stillingsannonsen" maxLength={100} showCount />
          </Form.Item>
          <Form.Item
            name="description"
            label="Beskrivelse"
            initialValue={job?.description}
            rules={[
              { required: true, message: "Vennligst skriv inn en beskrivelse" },
              {
                validator: (_, value) => {
                  if (!value) return Promise.resolve();
                  // Remove HTML tags and count text length
                  const textContent = value.replace(/<[^>]*>/g, "");
                  if (textContent.length > 8000) {
                    return Promise.reject(new Error("Beskrivelse kan ikke være lengre enn 8000 tegn"));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <ReactQuill />
          </Form.Item>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item label="Vedlegg">
                <Upload
                  listType="picture-card"
                  beforeUpload={file => {
                    setAttachment(file);
                    setRemoveAttachment(false);
                    return false;
                  }}
                  onRemove={() => {
                    setAttachment(null);
                    setAttachmentPreview(null);
                    setRemoveAttachment(true);
                  }}
                  fileList={
                    removeAttachment
                      ? []
                      : attachmentPreview && attachment
                      ? [
                          {
                            uid: "-1",
                            name: attachment.name || "Vedlegg",
                            status: "done",
                            url: attachmentPreview,
                          },
                        ]
                      : job?.attachment
                      ? [
                          {
                            uid: "-1",
                            name: job.attachment?.substring(job.attachment?.lastIndexOf("/") + 1) || "Vedlegg",
                            status: "done",
                            url: axios.defaults.baseURL + job.attachment,
                          },
                        ]
                      : []
                  }
                >
                  {uploadButton}
                </Upload>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Alternativ logo"
                help={
                  <>
                    <Text style={{ color: "grey", display: "block", marginBottom: "4px" }}>
                      Last opp en logo dersom du ønsker å benytte en annen logo enn bedriften sin egen.
                    </Text>
                    <Text style={{ color: "grey", fontSize: "12px" }}>Tillatte filtyper: JPG, PNG, GIF, WebP</Text>
                  </>
                }
                style={{ paddingBottom: "1.5em" }}
              >
                <Upload
                  listType="picture-card"
                  accept={IMAGE_ACCEPT}
                  beforeUpload={file => {
                    const result = beforeUploadImage(
                      file,
                      "Alternativ logo må være et bilde (JPG, PNG, GIF eller WebP)"
                    );
                    if (result === Upload.LIST_IGNORE) return result;
                    setLogo(file);
                    setRemoveLogo(false);
                    return false;
                  }}
                  onRemove={() => {
                    setLogo(null);
                    setLogoPreview(null);
                    setRemoveLogo(true);
                  }}
                  fileList={
                    removeLogo
                      ? []
                      : logoPreview
                      ? [
                          {
                            uid: "-1",
                            name: "Alternativ logo",
                            status: "done",
                            url: logoPreview,
                          },
                        ]
                      : job?.alt_logo
                      ? [
                          {
                            uid: "-1",
                            name: job.alt_logo?.substring(job.alt_logo?.lastIndexOf("/") + 1) || "Logo",
                            status: "done",
                            url: axios.defaults.baseURL + job.alt_logo,
                          },
                        ]
                      : []
                  }
                >
                  {uploadButton}
                </Upload>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="year_levels"
            label="Årstrinn"
            initialValue={job?.year_levels}
            rules={[{ required: true, message: "Vennligst velg årstrinn stillingen er aktuell for" }]}
          >
            <Checkbox.Group options={yearLevels} />
          </Form.Item>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                name="job_type"
                label="Stillingstype"
                initialValue={job?.job_type}
                rules={[{ required: true, message: "Vennligst velg stillingstype" }]}
              >
                <Select options={JOB_TYPE_DESCRIPTIONS} placeholder="Velg stillingstype" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="location"
                label="Arbeidssted"
                initialValue={job?.location}
                rules={[
                  { required: true, message: "Vennligst skriv inn et arbeidssted" },
                  { max: 100, message: "Arbeidssted kan ikke være lengre enn 100 tegn" },
                ]}
                help={<Text style={{ color: "grey" }}>Separer med komma</Text>}
                style={{ paddingBottom: "1.5em" }}
              >
                <Input placeholder="Arbeidsted for stillingen" maxLength={100} showCount />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="sectors"
            label="Bransje"
            initialValue={job?.sectors}
            rules={[{ required: true, message: "Vennligst velg minst en bransje stillingen er aktuell for" }]}
          >
            <Checkbox.Group>
              <Row style={{ minHeight: "66px" }}>{checkboxSectors}</Row>
            </Checkbox.Group>
          </Form.Item>
          <Form.Item name="company_contact" label="Kontaktperson" initialValue={job?.company_contact}>
            <Select
              placeholder="Velg kontaktperson for stillingen"
              dropdownRender={menu => (
                <>
                  {menu}
                  <Divider style={{ margin: "8px 0" }} />
                  <Space align="end" direction="vertical" style={{ padding: "0 8px 4px", float: "right" }}>
                    <CreateOrEditContact
                      companyId={existingJob ? job?.company : parseInt(id, 10)}
                      isOpen={showCreateContactModal}
                      onClose={() => setShowCreateContactModal(false)}
                      handleCreate={contact => setCompanyContacts(prevContacts => [...prevContacts, contact])}
                    />
                    <Button type="primary" onClick={() => setShowCreateContactModal(true)}>
                      Ny kontaktperson
                    </Button>
                  </Space>
                </>
              )}
            >
              {companyContacts?.map(contact => {
                return (
                  <Option key={contact.id} value={contact.id}>
                    {contact.first_name} {contact.last_name}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>
          <Row gutter={[16, 16]}>
            <Col span={12}>
              <Form.Item
                name="deadline"
                label="Søknadsfrist"
                initialValue={job?.deadline ? dayjs(job?.deadline) : null}
                rules={[{ required: true, message: "Vennligst velg en søknadsfrist" }, { type: "object" }]}
              >
                <DatePicker
                  format="DD.MM.YYYY"
                  placeholder="Velg dato"
                  disabledDate={current => {
                    return dayjs().add(-1, "days") >= current;
                  }}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="search_url"
                label="Link til søknadsskjema"
                initialValue={job?.search_url}
                rules={[
                  { type: "url", message: "Oppgi en gyldig url" },
                  { max: 500, message: "Link kan ikke være lengre enn 500 tegn" },
                ]}
              >
                <Input placeholder="Link til søknadsskjema (dersom det finnes)" maxLength={500} showCount />
              </Form.Item>
            </Col>
          </Row>
          <Paragraph style={{ fontSize: "0.8em", color: "grey", fontStyle: "italic", width: "100%" }}>
            Stillingen(e) som annonseres må kreve at søkere har påbegynt, eller fullført, høyere utdanning. Bindeleddet
            NTNU forbeholder seg retten til å fjerne annonser som ikke oppfyller dette kravet.
          </Paragraph>
          <Divider />
          <Row gutter={10}>
            <Col span={12}>
              <Form.Item name="is_visible" valuePropName="checked" initialValue={existingJob ? job?.is_visible : true}>
                <Checkbox>Vis på BL-sidene</Checkbox>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="is_kd" valuePropName="checked" initialValue={job?.is_kd}>
                <Checkbox>Vis på KD-sidene</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          <Divider style={{ marginTop: 0 }} />
          <Row style={{ justifyContent: "space-between", width: "100%", padding: "10px 0" }}>
            <Link to={`/admin/companies/${id}/`}>
              <Button type="primary" danger size="large">
                Avbryt
              </Button>
            </Link>
            <Button type="primary" size="large" loading={loading} htmlType="submit">
              {existingJob ? "Oppdater annonse" : "Opprett anonnse"}
            </Button>
          </Row>
        </Form>
      </Card>
    </>
  );
};

export const CreateJob: React.FC = () => <AdminJobs existingJob={false} />;
export const EditJob: React.FC = () => <AdminJobs existingJob />;
